import React, { useState, useEffect, useMemo } from 'react';
import { apiUrl, type UnitScale, unitLabel } from '../App';
import { authedFetch } from '../supabaseClient';
import {
  Treemap, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CustomerPeriod {
  revenue: number;
  is_active: boolean;
  movement: string | null;
}

interface CustomerRecord {
  customer_id: string;
  customer_name: string;
  segment: string;
  revenue_type: string;
  product: string;
  cohort_date: string;
  ending_date: string;
  ttm: number;
  arr: number;
  pct_arr: number;
  periods: Record<string, CustomerPeriod>;
}

interface SnapshotStats {
  min: number | null;
  p25: number | null;
  median: number | null;
  mean: number | null;
  p75: number | null;
  max: number | null;
  count: number;
}

interface DispersionRow {
  threshold: number;
  count: number;
  pct_revenue: number;
}

interface CustomerAnalyticsData {
  period_cols: string[];
  period_type: string;
  total_unfiltered_customers: number;
  filter_options: {
    segments: string[];
    revenue_types: string[];
    products: string[];
  };
  customers: CustomerRecord[];
  summary: {
    total_revenue: Record<string, number>;
    run_rate_revenue: Record<string, number>;
    ttm_revenue: Record<string, number | null>;
    yoy_growth: Record<string, number | null>;
    ttm_yoy_growth: Record<string, number | null>;
    customer_count: Record<string, number>;
    total_arr: number;
    snapshot: SnapshotStats;
    dispersion_ttm: DispersionRow[];
    dispersion_arr: DispersionRow[];
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  return `${parseInt(month)}/${year}`;
}

let _scale = 1000;

function formatVal(val: number): string {
  if (val === 0) return '';
  const abs = Math.abs(val) / _scale;
  const str = abs < 1 ? abs.toFixed(1) : Math.round(abs).toLocaleString();
  return val < 0 ? `(${str})` : str;
}

function formatPct(val: number | null | undefined): string {
  if (val === null || val === undefined) return '';
  const rounded = Math.round(Math.abs(val));
  const str = rounded === 0 ? '0%' : rounded + '%';
  return val < 0 ? `(${str})` : str;
}

function formatPctDecimal(val: number | null | undefined): string {
  if (val === null || val === undefined || val === 0) return '';
  return val.toFixed(1) + '%';
}

function formatDollar(val: number): string {
  if (val === 0) return '$0';
  if (val >= 1_000_000) return `$${(val / 1_000_000)}M`;
  if (val >= 1_000) return `$${(val / 1_000)}K`;
  return `$${val}`;
}

function formatSnapshotVal(val: number | null): string {
  if (val === null) return '—';
  return formatVal(val) || '0';
}

// ── Filter Bar ───────────────────────────────────────────────────────────────

function FilterBar({
  filterOptions,
  filters,
  onFiltersChange,
}: {
  filterOptions: CustomerAnalyticsData['filter_options'];
  filters: Record<string, string>;
  onFiltersChange: (f: Record<string, string>) => void;
}) {
  const update = (key: string, value: string) => {
    const next = { ...filters };
    if (value === '') {
      delete next[key];
    } else {
      next[key] = value;
    }
    onFiltersChange(next);
  };

  const dropdowns: { label: string; key: string; options: string[] }[] = [
    { label: 'Segment',      key: 'segment',      options: filterOptions.segments },
    { label: 'Revenue Type', key: 'revenue_type',  options: filterOptions.revenue_types },
    { label: 'Product',      key: 'product',       options: filterOptions.products },
  ];

  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4 mb-4">
      <span className="text-gray-500 text-xs font-medium">{unitLabel(_scale as UnitScale)}</span>
      {dropdowns.map(d => (
        <div key={d.key} className="flex items-center gap-2">
          <label className="text-gray-500 text-xs font-medium">{d.label}</label>
          <select
            value={filters[d.key] || ''}
            onChange={e => update(d.key, e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-blue-500 min-w-[120px] flex-1 md:flex-none"
          >
            <option value="">All</option>
            {d.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      ))}
      {Object.keys(filters).length > 0 && (
        <button
          onClick={() => onFiltersChange({})}
          className="text-gray-500 hover:text-white text-xs transition-colors ml-1"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

// ── Summary Bar ──────────────────────────────────────────────────────────────

function SummaryBar({ data }: { data: CustomerAnalyticsData }) {
  const { period_cols, summary } = data;
  if (period_cols.length === 0) return null;

  const lastP = period_cols[period_cols.length - 1];
  const runRate        = summary.run_rate_revenue[lastP] ?? 0;
  const ttm            = summary.ttm_revenue[lastP];
  const yoy            = summary.yoy_growth[lastP];
  const customerCount  = summary.customer_count[lastP] ?? 0;

  const cards = [
    { label: 'Run-Rate Revenue',                          value: formatVal(runRate) || '0' },
    { label: 'TTM Revenue',                               value: ttm != null ? (formatVal(ttm) || '0') : '—' },
    { label: 'YoY Growth',                                value: formatPct(yoy) || '—' },
    { label: 'Active Customers',                          value: customerCount.toLocaleString() },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      {cards.map(c => (
        <div key={c.label} className="bg-gray-800/50 border border-gray-800 rounded-lg px-3 md:px-4 py-2.5">
          <p className="text-gray-500 text-[11px] md:text-xs font-medium">{c.label}</p>
          <p className="text-white text-base md:text-lg font-bold mt-0.5">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

// ── Customer Table ───────────────────────────────────────────────────────────

function CustomerTable({ data, totalCustomerCount }: { data: CustomerAnalyticsData; totalCustomerCount?: number }) {
  const { period_cols, customers, period_type } = data;
  const n = period_type === 'quarterly' ? 4 : 12;
  const [sortKey, setSortKey] = useState<'arr' | 'ttm' | 'pct_arr' | 'cohort_date' | 'customer_name'>('arr');
  const [sortDesc, setSortDesc] = useState(true);

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDesc(!sortDesc);
    else { setSortKey(key); setSortDesc(true); }
  };

  const sorted = [...customers].sort((a, b) => {
    let av: any = a[sortKey], bv: any = b[sortKey];
    if (typeof av === 'string') return sortDesc ? bv.localeCompare(av) : av.localeCompare(bv);
    return sortDesc ? bv - av : av - bv;
  });

  const arrow = (key: string) => sortKey === key ? (sortDesc ? ' ▼' : ' ▲') : '';

  // Sticky column offsets
  const COL_NAME = 120;
  const COL_COHORT = 60;
  const COL_END = 60;

  // Total row label
  const totalLabel = totalCustomerCount && totalCustomerCount !== customers.length
    ? `Total (${customers.length} of ${totalCustomerCount})`
    : `Total (${customers.length})`;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg">
      <div className="overflow-auto max-h-[calc(100vh-24rem)]">
        <table className="w-full text-[11px] md:text-xs">
          <thead className="sticky top-0 z-20">
            <tr className="border-b border-gray-700 bg-gray-900">
              <th
                className="sticky left-0 z-30 bg-gray-900 text-left px-2 md:px-3 py-2 text-gray-400 font-medium cursor-pointer hover:text-white whitespace-nowrap"
                style={{ width: COL_NAME, minWidth: COL_NAME }}
                onClick={() => handleSort('customer_name')}
              >
                Customer{arrow('customer_name')}
              </th>
              <th
                className="sticky z-30 bg-gray-900 text-left px-2 py-2 text-gray-400 font-medium cursor-pointer hover:text-white whitespace-nowrap"
                style={{ left: COL_NAME, width: COL_COHORT, minWidth: COL_COHORT }}
                onClick={() => handleSort('cohort_date')}
              >
                Cohort{arrow('cohort_date')}
              </th>
              <th
                className="sticky z-30 bg-gray-900 text-left px-2 py-2 text-gray-400 font-medium whitespace-nowrap"
                style={{ left: COL_NAME + COL_COHORT, width: COL_END, minWidth: COL_END }}
              >
                Ending
              </th>
              {period_cols.map(p => (
                <th key={p} className="bg-gray-900 text-right px-2 md:px-4 py-2 font-bold text-gray-200 text-xs whitespace-nowrap">
                  {formatPeriod(p)}
                </th>
              ))}
              <th
                className="bg-gray-900 text-right px-2 md:px-4 py-2 text-gray-400 font-bold cursor-pointer hover:text-white whitespace-nowrap"
                onClick={() => handleSort('ttm')}
              >
                TTM{arrow('ttm')}
              </th>
              <th
                className="bg-gray-900 text-right px-2 md:px-4 py-2 text-gray-400 font-bold cursor-pointer hover:text-white whitespace-nowrap"
                onClick={() => handleSort('arr')}
              >
                RRR{arrow('arr')}
              </th>
              <th
                className="bg-gray-900 text-right px-2 md:px-4 py-2 text-gray-400 font-bold cursor-pointer hover:text-white whitespace-nowrap"
                onClick={() => handleSort('pct_arr')}
              >
                %RRR{arrow('pct_arr')}
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Total row */}
            <tr className="border-b border-gray-700 bg-[#131b2a]">
              <td
                className="sticky left-0 z-10 bg-[#131b2a] px-2 md:px-3 py-1.5 font-bold text-white whitespace-nowrap"
                style={{ width: COL_NAME, minWidth: COL_NAME }}
              >
                {totalLabel}
              </td>
              <td className="sticky z-10 bg-[#131b2a] px-2 py-1.5" style={{ left: COL_NAME }} />
              <td className="sticky z-10 bg-[#131b2a] px-2 py-1.5" style={{ left: COL_NAME + COL_COHORT }} />
              {period_cols.map(p => {
                const total = customers.reduce((sum, c) => sum + (c.periods[p]?.revenue ?? 0), 0);
                return (
                  <td key={p} className="text-right px-2 md:px-4 py-1.5 font-bold text-white whitespace-nowrap">
                    {formatVal(total)}
                  </td>
                );
              })}
              <td className="text-right px-2 md:px-4 py-1.5 font-bold text-white whitespace-nowrap">
                {formatVal(customers.reduce((s, c) => s + c.ttm, 0))}
              </td>
              <td className="text-right px-2 md:px-4 py-1.5 font-bold text-white whitespace-nowrap">
                {formatVal(customers.reduce((s, c) => s + c.arr, 0))}
              </td>
              <td className="text-right px-2 md:px-4 py-1.5 font-bold text-white whitespace-nowrap">100%</td>
            </tr>

            {/* Customer rows */}
            {sorted.map(c => (
              <tr key={c.customer_id} className="border-b border-gray-800/40 hover:bg-gray-800/30">
                <td
                  className="sticky left-0 z-10 bg-gray-900 px-2 md:px-3 py-1.5 text-gray-300 whitespace-nowrap truncate"
                  style={{ width: COL_NAME, minWidth: COL_NAME, maxWidth: COL_NAME }}
                  title={c.customer_name || c.customer_id}
                >
                  {c.customer_name || c.customer_id}
                </td>
                <td
                  className="sticky z-10 bg-gray-900 px-2 py-1.5 text-gray-500 whitespace-nowrap"
                  style={{ left: COL_NAME }}
                >
                  {formatPeriod(c.cohort_date)}
                </td>
                <td
                  className="sticky z-10 bg-gray-900 px-2 py-1.5 text-gray-500 whitespace-nowrap"
                  style={{ left: COL_NAME + COL_COHORT }}
                >
                  {formatPeriod(c.ending_date)}
                </td>
                {period_cols.map(p => (
                  <td key={p} className="text-right px-2 md:px-4 py-1.5 text-gray-300 whitespace-nowrap">
                    {formatVal(c.periods[p]?.revenue ?? 0)}
                  </td>
                ))}
                <td className="text-right px-2 md:px-4 py-1.5 font-bold text-gray-300 whitespace-nowrap">
                  {formatVal(c.ttm)}
                </td>
                <td className="text-right px-2 md:px-4 py-1.5 font-bold text-gray-300 whitespace-nowrap">
                  {formatVal(c.arr)}
                </td>
                <td className="text-right px-2 md:px-4 py-1.5 font-bold text-gray-400 whitespace-nowrap">
                  {formatPctDecimal(c.pct_arr)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Snapshot & Dispersion Panels ─────────────────────────────────────────────

function SnapshotPanel({ snapshot, periodType, customers }: { snapshot: SnapshotStats; periodType: string; customers: CustomerRecord[] }) {
  const n = periodType === 'quarterly' ? 4 : 12;
  const rows = [
    { label: 'Min RRR',     value: snapshot.min != null ? snapshot.min * n : null },
    { label: '25th Pctl',   value: snapshot.p25 != null ? snapshot.p25 * n : null },
    { label: 'Median',      value: snapshot.median != null ? snapshot.median * n : null },
    { label: 'Mean',        value: snapshot.mean != null ? snapshot.mean * n : null },
    { label: '75th Pctl',   value: snapshot.p75 != null ? snapshot.p75 * n : null },
    { label: 'Max RRR',     value: snapshot.max != null ? snapshot.max * n : null },
  ];

  // Concentration: Top 1 / Top 3 / Top 5 as % of total RRR
  const concentration = useMemo(() => {
    const sorted = [...customers].filter(c => c.arr > 0).sort((a, b) => b.arr - a.arr);
    const totalRRR = sorted.reduce((s, c) => s + c.arr, 0);
    if (totalRRR === 0) return { top1: null, top3: null, top5: null, top1Name: '' };
    const topN = (n: number) => sorted.slice(0, n).reduce((s, c) => s + c.arr, 0) / totalRRR * 100;
    return {
      top1: topN(1),
      top3: sorted.length >= 3 ? topN(3) : null,
      top5: sorted.length >= 5 ? topN(5) : null,
      top1Name: sorted[0]?.customer_name || sorted[0]?.customer_id || '',
    };
  }, [customers]);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-bold text-white mb-3">Run-Rate Revenue Snapshot</h3>
      <table className="w-full text-[11px] md:text-xs">
        <tbody>
          {rows.map(r => (
            <tr key={r.label} className="border-b border-gray-800/40">
              <td className="py-1.5 text-gray-500">{r.label}</td>
              <td className="py-1.5 text-right text-gray-300 font-medium">{formatSnapshotVal(r.value)}</td>
            </tr>
          ))}
          <tr className="border-b border-gray-800/40">
            <td className="py-1.5 text-gray-500 font-medium"># Customers</td>
            <td className="py-1.5 text-right text-white font-bold">{snapshot.count}</td>
          </tr>
          {concentration.top1 != null && (
            <tr className="border-b border-gray-800/40">
              <td className="py-1.5 text-gray-500" title={concentration.top1Name}>Top 1 Customer</td>
              <td className="py-1.5 text-right text-gray-300 font-medium">{concentration.top1.toFixed(1)}%</td>
            </tr>
          )}
          {concentration.top3 != null && (
            <tr className="border-b border-gray-800/40">
              <td className="py-1.5 text-gray-500">Top 3 Customers</td>
              <td className="py-1.5 text-right text-gray-300 font-medium">{concentration.top3.toFixed(1)}%</td>
            </tr>
          )}
          {concentration.top5 != null && (
            <tr>
              <td className="py-1.5 text-gray-500">Top 5 Customers</td>
              <td className="py-1.5 text-right text-gray-300 font-medium">{concentration.top5.toFixed(1)}%</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DispersionPanel({ title, rows }: { title: string; rows: DispersionRow[] }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-bold text-white mb-3">{title}</h3>
      <table className="w-full text-[11px] md:text-xs">
        <thead>
          <tr className="border-b border-gray-700">
            <th className="text-left py-1.5 text-gray-500 font-medium">≥ Threshold</th>
            <th className="text-right py-1.5 text-gray-500 font-medium"># Cust</th>
            <th className="text-right py-1.5 text-gray-500 font-medium">% Rev</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.threshold} className="border-b border-gray-800/40">
              <td className="py-1.5 text-gray-400">{formatDollar(r.threshold)}</td>
              <td className="py-1.5 text-right text-gray-300">{r.count}</td>
              <td className="py-1.5 text-right text-gray-300">{Math.round(r.pct_revenue)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Treemap Panel ───────────────────────────────────────────────────────────

// Custom treemap content renderer
function TreemapContent(props: any) {
  const { x, y, width, height, name, pctLabel, color } = props;
  if (width < 2 || height < 2) return null;

  const showName = width > 50 && height > 28;
  const showPct = width > 40 && height > 18;

  return (
    <g>
      <rect
        x={x} y={y} width={width} height={height}
        rx={3} ry={3}
        style={{
          fill: color || '#334155',
          stroke: '#111827',
          strokeWidth: 2,
        }}
      />
      {showName && (
        <text
          x={x + width / 2} y={y + height / 2 - (showPct ? 6 : 0)}
          textAnchor="middle" dominantBaseline="central"
          style={{ fill: '#e5e7eb', fontSize: Math.min(11, width / 8), fontWeight: 600 }}
        >
          {name.length > width / 7 ? name.slice(0, Math.floor(width / 7)) + '…' : name}
        </text>
      )}
      {showName && showPct && (
        <text
          x={x + width / 2} y={y + height / 2 + 10}
          textAnchor="middle" dominantBaseline="central"
          style={{ fill: '#9ca3af', fontSize: 9 }}
        >
          {pctLabel}
        </text>
      )}
    </g>
  );
}

function TreemapPanel({ customers }: { customers: CustomerRecord[] }) {
  // Distinct color palette — cycles through visually separated hues
  const TREEMAP_PALETTE = [
    '#3b82f6', '#0ea5e9', '#6366f1', '#14b8a6', '#2563eb',
    '#0891b2', '#8b5cf6', '#0d9488', '#0284c7', '#4f46e5',
    '#7c3aed', '#06b6d4',
  ];

  const treemapData = useMemo(() => {
    const active = customers.filter(c => c.arr > 0).sort((a, b) => b.arr - a.arr);
    const totalRRR = active.reduce((s, c) => s + c.arr, 0);
    return active.map((c, i) => ({
      name: c.customer_name || c.customer_id,
      value: c.arr,
      pctLabel: totalRRR > 0 ? `${(c.arr / totalRRR * 100).toFixed(1)}%` : '',
      color: TREEMAP_PALETTE[i % TREEMAP_PALETTE.length],
    }));
  }, [customers]);

  if (treemapData.length === 0) return null;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 flex flex-col">
      <h3 className="text-sm font-bold text-white mb-3">Customer Revenue Share</h3>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={treemapData}
            dataKey="value"
            aspectRatio={4 / 3}
            content={<TreemapContent />}
          >
            <Tooltip
              content={({ payload }) => {
                if (!payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-xl px-3 py-2 text-xs">
                    <p className="font-bold text-white">{d.name}</p>
                    <p className="text-gray-400">RRR: {(() => { const v = d.value; if (_scale === 1_000_000) return `$${(v / _scale).toFixed(1)}M`; return `$${Math.round(v / _scale).toLocaleString()}`; })()}</p>
                    <p className="text-gray-400">Share: {d.pctLabel}</p>
                  </div>
                );
              }}
            />
          </Treemap>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Pareto Chart ────────────────────────────────────────────────────────────

function ParetoChart({ customers }: { customers: CustomerRecord[] }) {
  const chartData = useMemo(() => {
    const active = customers.filter(c => c.arr > 0).sort((a, b) => b.arr - a.arr);
    const totalRRR = active.reduce((s, c) => s + c.arr, 0);
    let cumulative = 0;
    return active.map(c => {
      cumulative += c.arr;
      return {
        name: c.customer_name || c.customer_id,
        rrr: c.arr / _scale,
        rrrRaw: c.arr,
        cumPct: totalRRR > 0 ? Math.round(cumulative / totalRRR * 1000) / 10 : 0,
      };
    });
  }, [customers, _scale]);

  if (chartData.length === 0) return null;

  // Dynamic y-axis max: round up to a clean number
  const maxRRR = Math.max(...chartData.map(d => d.rrr));
  const yMax = Math.ceil(maxRRR * 1.1);

  // Format RRR for tooltip with $ sign and commas
  const fmtTooltipRRR = (raw: number) => {
    const v = raw / _scale;
    if (_scale === 1_000_000) return `$${v.toFixed(1)}M`;
    return `$${Math.round(v).toLocaleString()}`;
  };

  // Scale label for y-axis
  const scaleNote = _scale === 1 ? '$' : _scale === 1000 ? '$000' : '$M';

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-bold text-white mb-4">Customer Concentration (Pareto)</h3>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 40, bottom: 60, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis
            dataKey="name"
            tick={{ fill: '#6b7280', fontSize: 9 }}
            angle={-45}
            textAnchor="end"
            interval={0}
            height={70}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: '#6b7280', fontSize: 10 }}
            tickFormatter={(v) => v.toLocaleString()}
            domain={[0, yMax]}
            label={{ value: scaleNote, position: 'insideTopLeft', offset: -5, style: { fill: '#6b7280', fontSize: 10 } }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fill: '#6b7280', fontSize: 10 }}
            tickFormatter={(v) => `${v}%`}
            domain={[0, 100]}
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload?.[0]) return null;
              const d = payload[0].payload;
              return (
                <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-xl px-3 py-2 text-xs">
                  <p className="font-bold text-white">{d.name}</p>
                  <p className="text-gray-400">RRR: {fmtTooltipRRR(d.rrrRaw)}</p>
                  <p className="text-gray-400">Cumulative: {d.cumPct}%</p>
                </div>
              );
            }}
          />
          <Bar yAxisId="left" dataKey="rrr" fill="#3b82f6" radius={[2, 2, 0, 0]} />
          <Line
            yAxisId="right"
            dataKey="cumPct"
            type="monotone"
            stroke="#f59e0b"
            strokeWidth={2}
            dot={{ r: 3, fill: '#f59e0b' }}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <p className="text-xs text-gray-500 mt-2 px-1">
        Bars show each customer's Run-Rate Revenue, sorted largest to smallest. The orange line tracks the cumulative share of total revenue. A steep rise on the left indicates high customer concentration.
      </p>
    </div>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

export default function RevenueAnalysisView({
  projectId,
  filters,
  onFiltersChange,
  unitScale,
}: {
  projectId: string;
  filters: Record<string, string>;
  onFiltersChange: (f: Record<string, string>) => void;
  unitScale: UnitScale;
}) {
  _scale = unitScale;
  const [data, setData]       = useState<CustomerAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    // Build filter query params
    const extra: Record<string, string> = {};
    if (filters.segment)      extra.segment = filters.segment;
    if (filters.revenue_type) extra.revenue_type = filters.revenue_type;
    if (filters.product)      extra.product = filters.product;

    authedFetch(apiUrl('/api/customers/analytics', projectId, extra))
      .then(res => {
        if (!res.ok) throw new Error(res.status === 404 ? 'No customer data' : 'Fetch failed');
        return res.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [projectId, filters]);

  if (loading) return <p className="text-gray-500 text-sm">Loading...</p>;
  if (error) return (
    <div className="text-center py-16">
      <p className="text-gray-500 text-sm">{error === 'No customer data' ? 'No customer revenue data uploaded for this project.' : error}</p>
    </div>
  );
  if (!data) return null;

  return (
    <div className="space-y-4">
      <FilterBar
        filterOptions={data.filter_options}
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      <SummaryBar data={data} />

      <CustomerTable data={data} totalCustomerCount={data.total_unfiltered_customers} />

      {/* Snapshot + Dispersion + Treemap */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SnapshotPanel snapshot={data.summary.snapshot} periodType={data.period_type} customers={data.customers} />
        <DispersionPanel title="ACV Dispersion" rows={data.summary.dispersion_arr.filter(r => r.threshold <= 1_000_000)} />
        <TreemapPanel customers={data.customers} />
      </div>

      {/* Pareto concentration chart */}
      <ParetoChart customers={data.customers} />
    </div>
  );
}
